import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  InputAdornment,
  Radio,
  RadioGroup,
  IconButton,
  Checkbox,
  InputBase,
  keyframes
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import CustomDatePicker from './CustomDatePicker'
import { format } from 'date-fns'
import GooglePlacesAutocomplete from './GooglePlacesAutoComplete'
import GoogleSignInButton from './GoogleSignInButton'
import axiosClient from '../services/axiosClient'
import { trackFeatureUsage } from '../services/FeatureTrackingService'
import { useAuth } from '../hooks/useAuth'
import { GoogleAuthService } from '../services/GoogleAuthService'
import { router } from '@inertiajs/react'
import SecondaryButton from './SecondaryButton'
import { listingService } from '../services/ListingService'
import { ListingFormData } from '../types/listing_onboarding'
import union_logo from '../assets/Union.svg';
import EditAvailability from './EditAvailability';
import PhoneInput from './PhoneInput';
import LandlordPhotoPicker from './LandlordPhotoPicker';
import { PhotoMetadata } from '../types/listing_onboarding';
import { buildTourRequestPrefillPatch, type TourRequestPrefillInput } from '../utils/tourRequestToListingPrefill';
import { PENDING_LISTING_ID_KEY } from '../constants/pendingListing';

export { PENDING_LISTING_ID_KEY } from '../constants/pendingListing';

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;
const PROXIMITY_CACHE_TTL_MS = 30 * 60 * 1000;
const PROXIMITY_CACHE_PREFIX = 'proximity-cache:';

const normalizeAddress = (address: string) => address.trim().toLowerCase().replace(/\s+/g, ' ');
const getProximityCacheKey = (address: string) => `${PROXIMITY_CACHE_PREFIX}${normalizeAddress(address)}`;

interface LocalListingFormData {
  customerPhone: string;
  rent: string;
  dateAvailable: string;
  address: string;
  rentalType: string;
  leaseTypes: {
    month_to_month: boolean;
    fixed_term_lease: boolean;
    sublease: boolean;
  };
  petPolicy: {
    no_pets_allowed: boolean;
    cats_allowed: boolean;
    small_dogs_allowed: boolean;
    large_dogs_allowed: boolean;
  };
  bedrooms: string;
  bathrooms: string;
  sharedBathroom: string;
  laundry: string;
  femaleRoommates: string;
  maleRoommates: string;
  totalBathroomUsers: string;
  proximity: string;
  /** Binds the `listing_photos` question; photo data is in `pendingPhotos`. */
  listingPhotoStep: string;
  availability: WeeklySchedule;
  minimumMonthlyIncome: string;
  acceptsStudents: string;
  acceptsFirstTimeRenters: string;
  requiresOneYearEmployment: string;
  requiresBackgroundCreditCheck: string;
  title: string;
  description: string;
  timezone: string;
}

interface TimeRange {
  startTime: string;
  endTime: string;
  hasConflict?: boolean;
}

interface DaySchedule {
  enabled: boolean;
  timeRanges: TimeRange[];
}

interface WeeklySchedule {
  [key: string]: DaySchedule;
}

interface ProximityPlace {
  id: number;
  name: string;
  enabled: boolean;
  distance: string;
}

interface ProximityGroup {
  category: string;
  places: ProximityPlace[];
}

interface ListingOnboardingFormProps {
  isVisible: boolean;
  skipAvailability?: boolean;
  /** When set (e.g. from public tour request), seeds the form and skips duplicate questions */
  tourRequestPrefill?: TourRequestPrefillInput;
}

interface Question {
  id: string;
  title?: string;
  type: 'text' | 'number' | 'select' | 'date' | 'address' | 'radio' | 'switch' | 'counter' | 'proximity' | 'availability' | 'google_signin' | 'generated_content' | 'single_choice' | 'multi_select' | 'dashboard' | 'screening_link' | 'phone' | 'listing_photos';
  field: keyof LocalListingFormData;
  options?: { value: string; label: string }[];
  placeholder?: string;
  subtext?: string;
  validation?: (value: any) => string | null;
  conditional?: (formData: LocalListingFormData) => boolean;
}

const questions: Question[] = [
  {
    id: 'rent',
    title: 'How much is rent?',
    type: 'number',
    field: 'rent',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Rent is required';
      if (isNaN(Number(value))) return 'Rent must be a number';
      if (Number(value) < 0) return 'Rent must be positive';
      return null;
    }
  },
  {
    id: 'address',
    title: 'What is the property address?',
    type: 'address',
    field: 'address',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Address is required';
      return null;
    }
  },
  {
    id: 'dateAvailable',
    title: 'When is the property available?',
    type: 'date',
    field: 'dateAvailable',
    validation: (value) => {
      if (!value) return 'Date is required';
      return null;
    }
  },
  {
    id: 'availability',
    title: 'Set your tour availability',
    type: 'availability',
    field: 'availability',
    validation: (value) => {
      const schedule = value as WeeklySchedule;
      const hasEnabledDay = Object.values(schedule).some(day => day.enabled);
      if (!hasEnabledDay) {
        return 'Please enable at least one day for tour availability';
      }
      return null;
    }
  },
  {
    id: 'proximity',
    title: 'Select nearby places to include',
    type: 'proximity',
    field: 'proximity'
  },
  {
    id: 'rentalType',
    title: 'Type of Rental',
    type: 'single_choice',
    field: 'rentalType',
    options: [
      { value: 'entire_place', label: 'Entire Place' },
      { value: 'private_room', label: 'Private Room' }
    ],
    validation: (value) => {
      if (!value) return 'Please select a rental type';
      return null;
    }
  },
  {
    id: 'leaseTypes',
    title: 'Leases you offer',
    type: 'multi_select',
    field: 'leaseTypes',
    options: [
      { value: 'fixed_term_lease', label: 'Fixed Term Lease' },
      { value: 'month_to_month', label: 'Month to Month' },
      { value: 'sublease', label: 'Sublease' }
    ],
    validation: (value) => {
      const leaseTypes = value as { month_to_month: boolean; fixed_term_lease: boolean; sublease: boolean };
      if (!leaseTypes.month_to_month && !leaseTypes.fixed_term_lease && !leaseTypes.sublease) {
        return 'Please select at least one lease type';
      }
      return null;
    }
  },
  {
    id: 'petPolicy',
    title: 'What is your Pet Policy?',
    type: 'multi_select',
    field: 'petPolicy',
    options: [
      { value: 'no_pets_allowed', label: 'No pets allowed' },
      { value: 'cats_allowed', label: 'Cats allowed' },
      { value: 'small_dogs_allowed', label: 'Small dogs allowed' },
      { value: 'large_dogs_allowed', label: 'Large dogs allowed' }
    ],
    validation: (value) => {
      const petPolicy = value as { no_pets_allowed: boolean; cats_allowed: boolean; small_dogs_allowed: boolean; large_dogs_allowed: boolean };
      if (!petPolicy.no_pets_allowed && !petPolicy.cats_allowed && !petPolicy.small_dogs_allowed && !petPolicy.large_dogs_allowed) {
        return 'Please select at least one pet policy option';
      }
      return null;
    }
  },
  {
    id: 'laundry',
    title: 'Laundry',
    type: 'single_choice',
    field: 'laundry',
    options: [
      { value: 'in_unit', label: 'In Unit' },
      { value: 'in_building', label: 'In Building' },
      { value: 'no_laundry', label: 'None' }
    ],
    validation: (value) => {
      if (!value) return 'Please select a laundry option';
      return null;
    }
  },
  {
    id: 'bedrooms',
    title: 'How many bedrooms?',
    type: 'counter',
    field: 'bedrooms',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'bathrooms',
    title: 'How many bathrooms?',
    type: 'counter',
    field: 'bathrooms',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'sharedBathroom',
    title: 'Is this a shared bathroom?',
    type: 'single_choice',
    field: 'sharedBathroom',
    options: [
      { value: 'shared_bathroom', label: 'Yes' },
      { value: 'private_bathroom', label: 'No' }
    ],
    conditional: (formData) => formData.rentalType === 'private_room'
  },
  {
    id: 'totalBathroomUsers',
    title: 'How many people (including prospect) will be using the bathroom?',
    type: 'counter',
    field: 'totalBathroomUsers',
    conditional: (formData) => formData.sharedBathroom === 'shared_bathroom',
    validation: (value) => {
      if (!value || Number(value) < 1) return 'Please enter a valid number (minimum 1)';
      return null;
    }
  },
  {
    id: 'femaleRoommates',
    title: 'How many female roommates in the household?',
    type: 'counter',
    field: 'femaleRoommates',
    conditional: (formData) => formData.rentalType === 'private_room',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'maleRoommates',
    title: 'How many male roommates in the household?',
    type: 'counter',
    field: 'maleRoommates',
    conditional: (formData) => formData.rentalType === 'private_room',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'minimumMonthlyIncome',
    title: 'What is the minimum monthly income required?',
    type: 'number',
    field: 'minimumMonthlyIncome',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Minimum income is required';
      if (isNaN(Number(value))) return 'Income must be a number';
      if (Number(value) < 0) return 'Income must be positive';
      return null;
    }
  },
  {
    id: 'acceptsStudents',
    title: 'Do you accept students?',
    type: 'single_choice',
    field: 'acceptsStudents',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ],
    validation: (value) => {
      if (!value) return 'Please select whether you accept students';
      return null;
    }
  },
  {
    id: 'acceptsFirstTimeRenters',
    title: 'Do you accept first-time renters?',
    type: 'single_choice',
    field: 'acceptsFirstTimeRenters',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ],
    validation: (value) => {
      if (!value) return 'Please select whether you accept first-time renters';
      return null;
    }
  },
  {
    id: 'requiresOneYearEmployment',
    title: 'Do you require applicants to have held a job for at least one year?',
    type: 'single_choice',
    field: 'requiresOneYearEmployment',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ],
    validation: (value) => {
      if (!value) return 'Please select employment requirement';
      return null;
    }
  },
  {
    id: 'requiresBackgroundCreditCheck',
    title: 'Do you require background and credit checks?',
    type: 'single_choice',
    field: 'requiresBackgroundCreditCheck',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ],
    validation: (value) => {
      if (!value) return 'Please select background check requirement';
      return null;
    }
  },
  {
    id: 'listing_photos',
    title: 'Show off your listing!',
    subtext: 'Choose up to 5 photo',
    type: 'listing_photos',
    field: 'listingPhotoStep',
    validation: () => null
  },
  {
    id: 'generatedContent',
    title: 'Your Listing Description',
    type: 'generated_content',
    field: 'proximity' // This field won't be used for generated content
  },
  {
    id: 'customerPhone',
    title: 'What is your phone number?',
    type: 'phone',
    field: 'customerPhone',
    placeholder: '(555) 555 - 5555',
    subtext: 'Only used to send you notifications',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Phone number is required';
      const digits = value.replace(/\D/g, '');
      if (digits.length < 10) return 'Please enter a valid phone number';
      return null;
    }
  },
  {
    id: 'googleSignin',
    type: 'google_signin',
    field: 'proximity' // This field won't be used for Google sign-in
  },
  {
    id: 'screeningLink',
    title: '',
    type: 'screening_link',
    field: 'proximity' // This field won't be used for screening link
  },
  {
    id: 'dashboard',
    title: 'Your dashboard is ready!',
    type: 'dashboard',
    field: 'proximity'
  }
];

// GoogleSignInQuestion Component
const GoogleSignInQuestion = ({
  isAuthenticated
}: {
  onSignInSuccess: (response: any) => void;
  isAuthenticated: boolean;
}) => {
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {!isAuthenticated ? (
        <>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Screening link is nearly ready
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Create your screening link by signing in.
          </Typography>

        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'success.main' }}>
            Screening link is ready! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Click the button below to create.
          </Typography>
        </>
      )}
    </Box>
  );
};

// ScreeningLinkQuestion Component
const ScreeningLinkQuestion = ({
  screeningLink,
  createListingStatus
}: {
  screeningLink: string;
  createListingStatus: 'idle' | 'loading' | 'success' | 'error';
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(screeningLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {createListingStatus === 'loading' && (
        <>
          <CircularProgress sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Creating your screening link...
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            This may take a few moments.
          </Typography>
        </>
      )}

      {createListingStatus === 'success' && screeningLink && (
        <>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'success.main', textAlign: 'left' }}>
            Screening link is ready! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'left' }}>
            Share this link with potential tenants to schedule tours.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'left' }}>
            We will automatically add this to your listing description, when you proceed to your dashboard.
          </Typography>

          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            p: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            bgcolor: 'grey.50'
          }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                flex: 1
              }}
            >
              {screeningLink}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCopyLink}
              sx={{
                borderColor: copied ? 'success.main' : 'grey.400',
                color: copied ? 'success.main' : 'grey.700',
                '&:hover': {
                  borderColor: copied ? 'success.main' : 'grey.500',
                  bgcolor: copied ? 'success.50' : 'grey.100'
                }
              }}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Box>
        </>
      )}

      {createListingStatus === 'error' && (
        <>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'error.main' }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            We encountered an error creating your screening link. Please try again.
          </Typography>
        </>
      )}
    </Box>
  );
};

// DashboardReadyQuestion Component
const DashboardReadyQuestion = ({
  onGoToDashboard
}: {
  onGoToDashboard: () => void;
}) => {
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Box
        component="img"
        src={union_logo}
        alt="Union Logo"
        sx={{
          display: 'block',
          animation: `${waveAnimation} 2s ease-in-out infinite`,
          margin: '0 auto',
          width: '120px',
          marginBottom: '2rem'
        }}
      />

      <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', textAlign: 'left' }}>
        You now have access to powerful tools to manage your listing
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          borderRadius: 2,
          p: 3,
          mb: 4,
          maxWidth: { xs: '85%', sm: 500 },
          width: { xs: '85%', sm: 'auto' },
          margin: '0 auto',
          boxShadow: 1
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: 'left' }}>
          Dashboard Features
        </Typography>

        <Box sx={{ width: '100%' }}>
          <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
            <li style={{ display: 'flex', alignItems: 'left', marginBottom: 8 }}>
              <span style={{ fontSize: 30, marginRight: 12 }}>📅</span>
              <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400, textAlign: 'left' }}>
                See all tours in one place
              </Typography>
            </li>
            <li style={{ display: 'flex', alignItems: 'left', marginBottom: 8 }}>
              <span style={{ fontSize: 30, marginRight: 12 }}>📋</span>
              <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400, textAlign: 'left' }}>
                Copy your  descriptions and scheduling links
              </Typography>
            </li>
            <li style={{ display: 'flex', alignItems: 'left', marginBottom: 8 }}>
              <span style={{ fontSize: 30, marginRight: 12 }}>⚙️</span>
              <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400, textAlign: 'left' }}>
                Change availability and update screening criteria
              </Typography>
            </li>
          </ul>
        </Box>
      </Box>

      <Button
        variant="contained"
        size="large"
        onClick={onGoToDashboard}
        sx={{
          bgcolor: 'black',
          color: 'white',
          marginTop: "0.8rem",
          px: 3,
          py: 1,
          fontSize: '0.95rem',
          '&:hover': {
            bgcolor: 'grey.900',
            color: 'white'
          }
        }}
      >
        Go to Dashboard
      </Button>
    </Box>
  );
};

// SingleChoiceQuestion Component
const SingleChoiceQuestion = ({
  options,
  value,
  onSelect
}: {
  options: { value: string; label: string }[];
  value: string;
  onSelect: (value: string) => void;
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      {options.map(option => (
        <Button
          key={option.value}
          variant={value === option.value ? 'contained' : 'outlined'}
          onClick={() => onSelect(option.value)}
          sx={{
            width: '98%',
            padding: '1rem',
            textTransform: 'none',
            justifyContent: 'center',
            backgroundColor: value === option.value ? '#F5F5F5' : 'transparent',
            color: '#000',
            border: '2px solid #E5E5E5',
            boxShadow: 'none',
            fontSize: '1rem',
            borderColor: value === option.value ? '#000' : '#E5E5E5',
            '&:hover': {
              borderColor: '#000',
              backgroundColor: value === option.value ? '#F5F5F5' : 'transparent',
              color: '#000',
              boxShadow: 'none',
            },
          }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
};

// PetPolicyQuestion Component - handles special pet policy logic
const PetPolicyQuestion = ({
  options,
  value,
  onSelect
}: {
  options: { value: string; label: string }[];
  value: { [key: string]: boolean };
  onSelect: (values: { [key: string]: boolean }) => void;
}) => {
  const handleToggle = (optionValue: string) => {
    const currentValue = value;

    // Special logic for pet policy: if "no_pets_allowed" is selected, deselect all others
    if (optionValue === 'no_pets_allowed') {
      if (currentValue['no_pets_allowed']) {
        // If "no pets allowed" is already selected, just deselect it
        onSelect({ ...currentValue, 'no_pets_allowed': false });
      } else {
        // If "no pets allowed" is being selected, deselect all others and select only this
        onSelect({
          'no_pets_allowed': true,
          'cats_allowed': false,
          'small_dogs_allowed': false,
          'large_dogs_allowed': false
        });
      }
    } else {
      // For other options, if "no_pets_allowed" is currently selected, deselect it first
      const newValue = { ...currentValue, [optionValue]: !currentValue[optionValue] };

      // Remove "no_pets_allowed" if any other option is selected
      if (newValue[optionValue] && optionValue !== 'no_pets_allowed') {
        newValue['no_pets_allowed'] = false;
      }

      onSelect(newValue);
    }
  };

  const isChecked = (optionValue: string) => {
    return value[optionValue] || false;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      {options.map(option => (
        <FormControlLabel
          key={option.value}
          control={
            <Checkbox
              checked={isChecked(option.value)}
              onChange={() => handleToggle(option.value)}
              sx={{
                color: '#E5E5E5',
                '&.Mui-checked': {
                  color: '#000',
                },
              }}
            />
          }
          label={option.label}
          sx={{
            margin: 0,
            padding: '0.75rem',
            border: '1px solid #E5E5E5',
            borderRadius: '4px',
            backgroundColor: isChecked(option.value) ? '#F5F5F5' : 'transparent',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem',
              color: '#000',
            },
          }}
        />
      ))}
    </Box>
  );
};
// MultiSelectQuestion Component
const MultiSelectQuestion = ({
  options,
  value,
  onSelect
}: {
  options: { value: string; label: string }[];
  value: string[] | { [key: string]: boolean };
  onSelect: (values: string[] | { [key: string]: boolean }) => void;
}) => {
  const isObjectValue = typeof value === 'object' && !Array.isArray(value);

  const handleToggle = (optionValue: string) => {
    if (isObjectValue) {
      // Handle object format (for lease types and pet policy)
      const currentValue = value as { [key: string]: boolean };

      // Special logic for pet policy: if "no_pets_allowed" is selected, deselect all others
      if (optionValue === 'no_pets_allowed') {
        if (currentValue['no_pets_allowed']) {
          // If "no pets allowed" is already selected, just deselect it
          onSelect({ ...currentValue, 'no_pets_allowed': false });
        } else {
          // If "no pets allowed" is being selected, deselect all others and select only this
          onSelect({
            'no_pets_allowed': true,
            'cats_allowed': false,
            'small_dogs_allowed': false,
            'large_dogs_allowed': false
          });
        }
      } else {
        // For other options, if "no_pets_allowed" is currently selected, deselect it first
        const newValue = { ...currentValue, [optionValue]: !currentValue[optionValue] };

        // Remove "no_pets_allowed" if any other option is selected
        if (newValue[optionValue] && optionValue !== 'no_pets_allowed') {
          newValue['no_pets_allowed'] = false;
        }

        onSelect(newValue);
      }
    } else {
      // Handle array format (legacy support)
      const currentValue = value as string[];

      // Special logic for pet policy: if "no_pets_allowed" is selected, deselect all others
      if (optionValue === 'no_pets_allowed') {
        if (currentValue.includes('no_pets_allowed')) {
          // If "no pets allowed" is already selected, just deselect it
          onSelect(currentValue.filter(v => v !== 'no_pets_allowed'));
        } else {
          // If "no pets allowed" is being selected, deselect all others and select only this
          onSelect(['no_pets_allowed']);
        }
      } else {
        // For other options, if "no_pets_allowed" is currently selected, deselect it first
        let newValues = currentValue.includes(optionValue)
          ? currentValue.filter(v => v !== optionValue)
          : [...currentValue, optionValue];
        
        // Remove "no_pets_allowed" if any other option is selected
        newValues = newValues.filter(v => v !== 'no_pets_allowed');
        onSelect(newValues);
      }
    }
  };

  const isChecked = (optionValue: string) => {
    if (isObjectValue) {
      return (value as { [key: string]: boolean })[optionValue] || false;
    } else {
      return (value as string[]).includes(optionValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      {options.map(option => (
        <FormControlLabel
          key={option.value}
          control={
            <Checkbox
              checked={isChecked(option.value)}
              onChange={() => handleToggle(option.value)}
              sx={{
                color: '#E5E5E5',
                '&.Mui-checked': {
                  color: '#000',
                },
              }}
            />
          }
          label={option.label}
          sx={{
            margin: 0,
            padding: '0.75rem',
            border: '1px solid #E5E5E5',
            borderRadius: '4px',
            backgroundColor: isChecked(option.value) ? '#F5F5F5' : 'transparent',
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem',
              color: '#000',
            },
          }}
        />
      ))}
    </Box>
  );
};

// GeneratedContentQuestion Component
const GeneratedContentQuestion = ({
  backgroundContent,
  onCopyTitle,
  onCopyDescription,
  copiedTitle,
  copiedDescription,
  currentTitle,
  currentDescription,
  onTitleChange,
  onDescriptionChange
}: {
  backgroundContent: { title: string; description: string } | null;
  onCopyTitle: () => void;
  onCopyDescription: () => void;
  copiedTitle: boolean;
  copiedDescription: boolean;
  currentTitle: string;
  currentDescription: string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
}) => {
  if (!backgroundContent) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>Generating your listing description...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Title Section */}
      <Typography variant="caption" sx={{ mb: 2 }}>Feel free to edit the title and description</Typography>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          value={currentTitle || backgroundContent.title}
          onChange={(e) => onTitleChange(e.target.value)}
          variant="outlined"
          placeholder="Enter listing title"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <SecondaryButton
            size="small"
            variant="contained"
            onClick={onCopyTitle}
          >
            {copiedTitle ? 'Copied!' : 'Copy Title'}
          </SecondaryButton>
        </Box>
      </Box>

      {/* Description Section */}
      <Box>
        <TextField
          fullWidth
          multiline
          rows={18}
          value={currentDescription || backgroundContent.description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          variant="outlined"
          placeholder="Enter listing description"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <SecondaryButton
            size="small"
            variant="contained"
            onClick={onCopyDescription}
          >
            {copiedDescription ? 'Copied!' : 'Copy Description'}
          </SecondaryButton>
        </Box>
      </Box>
    </Box>
  );
};

export default function ListingOnboardingForm({
  isVisible,
  skipAvailability = false,
  tourRequestPrefill,
}: ListingOnboardingFormProps) {
  const { user, isAuthenticated: authIsAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [tourSkippedQuestionIds, setTourSkippedQuestionIds] = useState<Set<string>>(new Set());
  const [pendingPhotos, setPendingPhotos] = useState<PhotoMetadata[]>([]);
  const [pendingListingId, setPendingListingId] = useState<string | null>(null);
  const [pendingListingIdLoading, setPendingListingIdLoading] = useState(false);
  const [pendingListingIdError, setPendingListingIdError] = useState<string | null>(null);
  const [listingPhotosError, setListingPhotosError] = useState<string>('');
  const [generatedContent, setGeneratedContent] = useState<{ title: string; description: string } | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [proximityPlaces, setProximityPlaces] = useState<ProximityPlace[]>([]);
  const [proximityGroups, setProximityGroups] = useState<ProximityGroup[] | null>(null);
  const [isProximityLoading, setIsProximityLoading] = useState(false);
  const [copiedTitle, setCopiedTitle] = useState(false);
  const [copiedDescription, setCopiedDescription] = useState(false);
  const [editingPlaces, setEditingPlaces] = useState<{ [id: number]: boolean }>({});
  const [backgroundRequestsTriggered, setBackgroundRequestsTriggered] = useState<{
    proximityData: boolean;
    listingContent: boolean;
    createLink: boolean;
  }>({
    proximityData: false,
    listingContent: false,
    createLink: false
  });
  const [backgroundGeneratedContent, setBackgroundGeneratedContent] = useState<{ title: string; description: string } | null>(null);
  const [createListingStatus, setCreateListingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [screeningLink, setScreeningLink] = useState<string>('');

  const miles = (d: number | string) => {
    const n = Number(d);
    return Number.isFinite(n) ? `${n.toFixed(1)} mi` : String(d);
  };

  const [formData, setFormData] = useState<LocalListingFormData>({
    customerPhone: '',
    rent: '',
    dateAvailable: format(new Date(), 'yyyy-MM-dd'),
    address: '',
    rentalType: '',
    leaseTypes: {
      month_to_month: false,
      fixed_term_lease: false,
      sublease: false
    },
    petPolicy: {
      no_pets_allowed: false,
      cats_allowed: false,
      small_dogs_allowed: false,
      large_dogs_allowed: false
    },
    bedrooms: '1',
    bathrooms: '1',
    sharedBathroom: '',
    laundry: '',
    femaleRoommates: '0',
    maleRoommates: '0',
    totalBathroomUsers: '1',
    proximity: '',
    listingPhotoStep: '',
    availability: {
      'SUN': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'MON': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'TUE': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'WED': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'THU': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'FRI': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
      'SAT': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] }
    },
    minimumMonthlyIncome: '',
    acceptsStudents: '',
    acceptsFirstTimeRenters: '',
    requiresOneYearEmployment: '',
    requiresBackgroundCreditCheck: '',
    title: '',
    description: '',
    timezone: 'America/New_York'
  });

  // Get visible questions based on conditional logic, skipAvailability, and tour prefill
  const visibleQuestions = questions.filter((q) => {
    if (tourSkippedQuestionIds.has(q.id)) {
      return false;
    }
    if (skipAvailability && q.id === 'availability') {
      return false;
    }
    return !q.conditional || q.conditional(formData);
  });

  const currentQuestionId = visibleQuestions[currentStep]?.id

  useEffect(() => {
    if (!tourRequestPrefill) {
      return;
    }
    const { formPatch, skippedIds } = buildTourRequestPrefillPatch(tourRequestPrefill);
    if (tourRequestPrefill.pending_listing_id) {
      setPendingListingId(tourRequestPrefill.pending_listing_id);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PENDING_LISTING_ID_KEY, tourRequestPrefill.pending_listing_id);
      }
    }
    setTourSkippedQuestionIds(skippedIds);
    setFormData((prev) => ({ ...prev, ...formPatch }));
  }, [tourRequestPrefill]);

  useEffect(() => {
    if (currentQuestionId !== 'listing_photos') {
      return;
    }
    if (pendingListingId || pendingListingIdLoading) {
      return;
    }
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(PENDING_LISTING_ID_KEY);
      if (stored) {
        setPendingListingId(stored);
        return;
      }
    }
    setPendingListingIdLoading(true);
    setPendingListingIdError(null);
    listingService
      .createPendingListing()
      .then((id) => {
        setPendingListingId(id);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(PENDING_LISTING_ID_KEY, id);
        }
      })
      .catch(() => {
        setPendingListingIdError('Could not prepare photo upload. Please check your connection and try again.');
      })
      .finally(() => {
        setPendingListingIdLoading(false);
      });
  }, [currentQuestionId, pendingListingId, pendingListingIdLoading]);

  const listingPhotosHydratedFromServerRef = useRef(false);
  // If a pending listing already has server-side photos, hydrate the picker when this step is shown
  useEffect(() => {
    if (currentQuestionId !== "listing_photos") {
      listingPhotosHydratedFromServerRef.current = false;
      return;
    }
    if (!pendingListingId || pendingListingIdLoading) {
      return;
    }
    if (listingPhotosHydratedFromServerRef.current) {
      return;
    }
    listingPhotosHydratedFromServerRef.current = true;
    listingService
      .getPendingListingPhotos(pendingListingId)
      .then((remote) => {
        if (remote.length > 0) {
          setPendingPhotos(remote);
        }
      })
      .catch(() => { });
  }, [currentQuestionId, pendingListingId, pendingListingIdLoading]);

  // When address is prefilled (e.g. tour) the address step is skipped, so handleNext never triggers
  // fetchProximityData. Load when we first show the proximity step; normal flow has proximityData
  // already set from completing the address step, so this no-ops.
  useEffect(() => {
    if (currentQuestionId !== "proximity") {
      return;
    }
    if (!formData.address?.trim()) {
      return;
    }
    if (proximityPlaces.length > 0) {
      return;
    }
    if (typeof window !== 'undefined') {
      const cachedRaw = sessionStorage.getItem(getProximityCacheKey(formData.address));
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as {
            fetchedAt?: number;
            grouped_places?: ProximityGroup[] | null;
            places?: ProximityPlace[] | null;
          };
          const isFresh = typeof cached.fetchedAt === 'number' && (Date.now() - cached.fetchedAt) < PROXIMITY_CACHE_TTL_MS;
          if (isFresh) {
            const grouped = Array.isArray(cached.grouped_places) ? cached.grouped_places : null;
            const places = Array.isArray(cached.places) ? cached.places : null;
            if (grouped && grouped.length > 0) {
              setProximityGroups(grouped);
              setProximityPlaces(grouped.flatMap(g => g.places));
              return;
            }
            if (places && places.length > 0) {
              setProximityGroups(null);
              setProximityPlaces(places);
              return;
            }
          } else {
            sessionStorage.removeItem(getProximityCacheKey(formData.address));
          }
        } catch (_) {
          sessionStorage.removeItem(getProximityCacheKey(formData.address));
        }
      }
    }
    if (backgroundRequestsTriggered.proximityData) {
      return;
    }
    setBackgroundRequestsTriggered((prev) => ({ ...prev, proximityData: true }));
    void fetchProximityData();
  }, [currentQuestionId, formData.address, proximityPlaces.length, backgroundRequestsTriggered.proximityData]);

  const currentQuestion = visibleQuestions[currentStep];

  // Memoize date value to prevent DatePicker from resetting
  const memoizedDateValue = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'date') return null;
    const value = formData[currentQuestion.field];
    return value ? new Date(`${value}T00:00:00`) : null;
  }, [currentQuestion, formData]);

  // Handle case where user is already authenticated on Google sign-in step

  const handleInputChange = (field: keyof LocalListingFormData, value: string | boolean | WeeklySchedule | string[] | { [key: string]: boolean }) => {
    // Debug logging for availability changes
    if (field === 'availability') {
      console.log('🔄 Availability changed in form:', value);
      console.log('🔄 Previous formData.availability:', formData.availability);
    }
    if (field === 'timezone') {
      console.log('🔄 Timezone changed in form:', value);
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value as any
      };
      if (field === 'availability') {
        console.log('🔄 New formData after setState:', newData.availability);
      }
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCopyTitle = async () => {
    try {
      const title = (generatedContent || backgroundGeneratedContent)?.title?.trim() || '';
      if (!title) return;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(title);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = title;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedTitle(true);
      trackFeatureUsage(user?.id ?? null, 'Copy Title');
      setTimeout(() => setCopiedTitle(false), 2000);
    } catch (_) { }
  };

  const handleCopyDescription = async () => {
    try {
      const description = (generatedContent || backgroundGeneratedContent)?.description?.trim() || '';
      if (!description) return;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(description);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = description;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedDescription(true);
      trackFeatureUsage(user?.id ?? null, 'Copy Description - Marketing');
      setTimeout(() => setCopiedDescription(false), 2000);
    } catch (_) { }
  };

  const toggleEditing = (id: number, on?: boolean) => {
    setEditingPlaces(prev => ({ ...prev, [id]: on === undefined ? !prev[id] : on }));
  };

  const handleGoogleSignIn = useCallback(async (response: any) => {
    if (response.credential) {
      console.log("Response Credential is:", response.credential);
      try {
        await GoogleAuthService.handleGoogleResponse(response, () => {
          console.log('Google sign-in successful');
          router.reload();
        });
      } catch (error) {
        console.error('Google sign-in failed:', error);
      }
    }
  }, []);

  const validateCurrentStep = () => {
    if (!currentQuestion) return true;

    // Skip validation for proximity step
    if (currentQuestion.type === 'proximity') {
      return true;
    }

    // Skip validation for Google sign-in step
    if (currentQuestion.type === 'google_signin') {
      return true;
    }

    if (currentQuestion.type === 'listing_photos') {
      if (pendingListingIdLoading || !pendingListingId) {
        return false;
      }
      if (pendingPhotos.length < 1) {
        return false;
      }
      if (pendingPhotos.length > 5) {
        return false;
      }
      if (!pendingPhotos.some((p) => p.isFeatured)) {
        return false;
      }
      return true;
    }

    const validation = currentQuestion.validation;
    if (!validation) return true;

    const error = validation(formData[currentQuestion.field]);
    if (error) {
      setErrors(prev => ({ ...prev, [currentQuestion.field]: error }));
      return false;
    }

    return true;
  };

  const convertToApiFormat = (localData: LocalListingFormData): ListingFormData => {
    const { listingPhotoStep: _listingPhotoStep, ...localForApi } = localData;
    return {
      ...localForApi,
      full_address: localData.address,
      google_place_id: '', // This would need to be set if available
      lease_types: localData.leaseTypes,
      pet_policy: localData.petPolicy,
      rental_type: localData.rentalType,
      property_type: 'apartment', // Default to apartment - could be made configurable later
      num_bedrooms: localData.bedrooms,
      num_bathrooms: localData.bathrooms,
      male_count: localData.maleRoommates,
      female_count: localData.femaleRoommates,
      shared_bathroom_count: localData.totalBathroomUsers,
      laundry_type: localData.laundry,
      date_available: localData.dateAvailable,
      rent: localData.rent,
      security_deposit: '0', // Default to 0 - could be made configurable later
      description: localData.description || 'Generated listing description', // Use user's edited description or fallback
      title: localData.title || 'Generated listing title', // Use user's edited title or fallback
      availability: localData.availability,
      timezone: localData.timezone,
      customer_phone: localData.customerPhone
    };
  };

  const createLink = async () => {
    setCreateListingStatus('loading');
    // Debug logging for availability data
    console.log('🚀 Creating listing with availability data:', formData.availability);
    console.log('🚀 Timezone:', formData.timezone);

    // Convert form data to API format
    const apiFormData = convertToApiFormat(formData);
    // Make the API call in the background
    listingService.createV2(apiFormData, {
      pendingListingId: pendingListingId || undefined,
      onSuccess: (data) => {
        console.log('Create listing successful:', data);
        sessionStorage.setItem('listing-id', data.listingId);
        // Generate the screening link using the actual listing ID from the response
        const link = `heyyproperty.com/schedule-tour/${data.listingId}`;
        setScreeningLink(link);
        setCreateListingStatus('success');
      },
      onError: (error) => {
        console.error('Create listing failed:', error);
        setCreateListingStatus('error');
      }
    });
  };

  const handleGoToDashboard = () => {
    const listingId = sessionStorage.getItem('listing-id');
    if (listingId) {
      router.visit(`/listings/${listingId}`);
    } else {
      console.error('No listing ID found in session storage');
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      const vq = visibleQuestions[currentStep];
      if (vq?.type === 'listing_photos') {
        if (pendingListingIdLoading || !pendingListingId) {
          setListingPhotosError('Preparing photo upload. Please wait a moment.');
        } else if (pendingPhotos.length < 1) {
          setListingPhotosError('Add at least one photo (up to 5).');
        } else if (pendingPhotos.length > 5) {
          setListingPhotosError('You can have at most 5 photos. Remove one to continue.');
        } else if (!pendingPhotos.some((p) => p.isFeatured)) {
          setListingPhotosError('Select a feature photo (tap the star on an image).');
        } else {
          setListingPhotosError('Please check your photos and try again.');
        }
      }
      return;
    }
    setListingPhotosError('');

    const currentQuestion = visibleQuestions[currentStep];
      // Special case: Create Link button on google_signin step
      if (currentQuestion?.type === 'google_signin' && authIsAuthenticated && !backgroundRequestsTriggered.createLink) {
        setBackgroundRequestsTriggered(prev => ({ ...prev, createLink: true }));
        createLink();
        // Immediately advance to screening link step
        if (currentStep < visibleQuestions.length - 1) {
          setCurrentStep(currentStep + 1);
        }
        return;
      }

      if (currentStep < visibleQuestions.length - 1) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);

        // Trigger background requests based on completed questions
        const currentQuestionId = visibleQuestions[currentStep]?.id;

        // Trigger fetchProximityData after address question is completed
        if (currentQuestionId === 'address' && !backgroundRequestsTriggered.proximityData) {
          setBackgroundRequestsTriggered(prev => ({ ...prev, proximityData: true }));
          // Trigger in background without blocking
          setTimeout(() => {
            fetchProximityData();
          }, 100);
        }

        if (currentQuestionId === 'minimumMonthlyIncome' && !backgroundRequestsTriggered.listingContent) {
          setBackgroundRequestsTriggered(prev => ({ ...prev, listingContent: true }));
          // Trigger in background without blocking
          setTimeout(() => {
            generateListingContent();
          }, 100);
        }

        // Copy background content to display content when reaching generated content step
        if (visibleQuestions[nextStep]?.type === 'generated_content' && backgroundGeneratedContent) {
          setGeneratedContent(backgroundGeneratedContent);
        }
      }
  };



  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
    }
  };

  const fetchProximityData = async () => {
    setIsProximityLoading(true);
    try {
      const response = await axiosClient.post('/api/v1/marketing_listing/fetch_proximity_data', {
        full_address: formData.address
      });
      if (typeof window !== 'undefined' && formData.address.trim()) {
        const groupedForCache = Array.isArray(response.data?.grouped_places) ? response.data.grouped_places : null;
        const rawForCache = response.data?.places;
        const placesForCache = Array.isArray(rawForCache) ? rawForCache : Array.isArray(response.data) ? response.data : null;
        sessionStorage.setItem(
          getProximityCacheKey(formData.address),
          JSON.stringify({
            fetchedAt: Date.now(),
            grouped_places: groupedForCache,
            places: Array.isArray(placesForCache) ? placesForCache : null,
          })
        );
      }
      const grouped = response.data.grouped_places as ProximityGroup[] | undefined;
      if (grouped && Array.isArray(grouped) && grouped.length > 0) {
        setProximityGroups(grouped);
        const flattened = grouped.flatMap(g => g.places);
        setProximityPlaces(flattened);
      } else {
        setProximityGroups(null);
        const raw = response.data?.places;
        const asArray = Array.isArray(raw) ? raw : Array.isArray(response.data) ? response.data : null;
        if (asArray && asArray.length > 0) {
          setProximityPlaces(asArray);
        } else {
          // API returned no rows — show defaults so the step is usable
          setProximityPlaces([
            { id: 1, name: "Nearby parks and green spaces", enabled: true, distance: "within walking distance" },
            { id: 2, name: "Local cafes and restaurants", enabled: true, distance: "within walking distance" },
            { id: 3, name: "Public transportation", enabled: true, distance: "within walking distance" },
            { id: 4, name: "Shopping centers", enabled: true, distance: "within walking distance" }
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching proximity data:', error);
      // Fallback places
      setProximityPlaces([
        { id: 1, name: "Nearby parks and green spaces", enabled: true, distance: "within walking distance" },
        { id: 2, name: "Local cafes and restaurants", enabled: true, distance: "within walking distance" },
        { id: 3, name: "Public transportation", enabled: true, distance: "within walking distance" },
        { id: 4, name: "Shopping centers", enabled: true, distance: "within walking distance" }
      ]);
      setProximityGroups(null);
    } finally {
      setIsProximityLoading(false);
    }
  };

  const generateListingContent = async () => {
    try {
      // Filter only enabled places
      const selectedPlaces = proximityPlaces.filter(place => place.enabled);

      // Prepare the data in the same format as the listing onboarding flow
      const requestData = {
        full_address: formData.address,
        rent: formData.rent,
        rental_type: formData.rentalType,
        lease_types: formData.leaseTypes,
        pet_policy: formData.petPolicy,
        num_bedrooms: formData.bedrooms,
        num_bathrooms: formData.bathrooms,
        laundry_type: formData.laundry,
        shared_bathroom_count: formData.totalBathroomUsers,
        has_private_bathroom: formData.sharedBathroom === 'private_bathroom',
        female_roommates: formData.femaleRoommates,
        male_roommates: formData.maleRoommates,
        date_available: formData.dateAvailable,
        selected_places: selectedPlaces
      };

      const response = await axiosClient.post('/api/v1/marketing_listing/generate_listing', requestData);

      const data = response.data;
      setBackgroundGeneratedContent({
        title: data.title,
        description: data.description
      });
      // Also update the form data so user can edit it
      setFormData((prev) => ({
        ...prev,
        title: data.title,
        description: data.description
      }));

    } catch (error) {
      console.error('Error generating listing content:', error);
      // Fallback to basic content if API fails
      const {
        rent,
        address,
        rentalType,
        leaseTypes,
        petPolicy,
        bedrooms,
        bathrooms,
        sharedBathroom,
        laundry,
        dateAvailable,
        femaleRoommates,
        maleRoommates,
        totalBathroomUsers,
        availability,
        minimumMonthlyIncome,
        acceptsStudents,
        acceptsFirstTimeRenters,
        requiresOneYearEmployment,
        requiresBackgroundCreditCheck
      } = formData;

      const rentalTypeDisplay = rentalType === 'entire_place' ? 'Property' : rentalType === 'private_room' ? 'Private Room' : rentalType;
      const title = `$${rent} | ${bedrooms}BR, ${bathrooms}BA ${rentalTypeDisplay} in ${address.split(',')[0]}`;

      let description = `🏠 Welcome to your new home! This beautiful ${rentalTypeDisplay} is now available for rent.

📍 **Location**: ${address}
💰 **Rent**: $${rent}/month
📅 **Available**: ${dateAvailable}
🛏️ **Bedrooms**: ${bedrooms}
🛁 **Bathrooms**: ${bathrooms}${sharedBathroom === 'shared_bathroom' ? ' (Shared bathroom)' : ''}
🧺 **Laundry**: ${laundry}`;

      // Add lease types information
      if (leaseTypes) {
        const leaseTypeLabels = {
          'month_to_month': 'Month to Month',
          'fixed_term_lease': 'Fixed Term Lease',
          'sublease': 'Sublease'
        };
        const selectedLeaseTypes = Object.entries(leaseTypes)
          .filter(([_, selected]) => selected)
          .map(([key, _]) => leaseTypeLabels[key as keyof typeof leaseTypeLabels] || key);

        if (selectedLeaseTypes.length > 0) {
          description += `\n📋 **Lease Types**: ${selectedLeaseTypes.join(', ')}`;
        }
      }

      // Add pet policy information
      if (petPolicy) {
        const petPolicyLabels = {
          'no_pets_allowed': 'No pets allowed',
          'cats_allowed': 'Cats allowed',
          'small_dogs_allowed': 'Small dogs allowed',
          'large_dogs_allowed': 'Large Dogs'
        };
        const selectedPetPolicies = Object.entries(petPolicy)
          .filter(([_, selected]) => selected)
          .map(([key, _]) => petPolicyLabels[key as keyof typeof petPolicyLabels] || key);

        if (selectedPetPolicies.length > 0) {
          description += `\n🐾 **Pet Policy**: ${selectedPetPolicies.join(', ')}`;
        }
      }

      // Add roommate information for private rooms
      if (rentalType === 'private_room') {
        if (sharedBathroom === 'shared_bathroom') {
          const totalRoommates = parseInt(femaleRoommates || '0') + parseInt(maleRoommates || '0');
          description += `\n👥 **Roommates**: ${totalRoommates} total (${femaleRoommates || '0'} female, ${maleRoommates || '0'} male)`;
        } else {
          description += `\n👥 **Bathroom Users**: ${totalBathroomUsers} people sharing this bathroom`;
        }

        // Add availability information
        const enabledDays = Object.entries(availability).filter(([_, day]) => day.enabled);
        if (enabledDays.length > 0) {
          description += `\n📅 **Tour Availability**: Available for tours on ${enabledDays.map(([day, _]) => {
            const dayNames = { 'MON': 'Monday', 'TUE': 'Tuesday', 'WED': 'Wednesday', 'THU': 'Thursday', 'FRI': 'Friday', 'SAT': 'Saturday', 'SUN': 'Sunday' };
            return dayNames[day as keyof typeof dayNames] || day;
          }).join(', ')}`;
        }

        // Add screening criteria information
        if (minimumMonthlyIncome) {
          description += `\n💰 **Minimum Income**: $${minimumMonthlyIncome}/month`;
        }
        if (acceptsStudents === 'false') {
          description += `\n🎓 **Students**: Not accepted`;
        }
        if (acceptsFirstTimeRenters === 'false') {
          description += `\n🏠 **First-time renters**: Not accepted`;
        }
        if (requiresOneYearEmployment === 'true') {
          description += `\n💼 **Employment**: 1 year minimum required`;
        }
        if (requiresBackgroundCreditCheck === 'true') {
          description += `\n🔍 **Background & Credit Check**: Required`;
        }
      }

      description += `\n\nThis property offers the perfect blend of comfort and convenience. Don't miss out on this opportunity to find your ideal home!

Contact us today to schedule a viewing!`;

      setBackgroundGeneratedContent({ title, description });

      // Also update the form data so user can edit it
      setFormData((prev) => ({
        ...prev,
        title: title,
        description: description
      }));
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;
    console.log("Current Question", currentQuestion);
    console.log("IsAuthenticated", authIsAuthenticated);
    console.log("User", user);

    const { type, field, options, placeholder } = currentQuestion;
    const value = formData[field];
    const error = errors[field];

    switch (type) {
      case 'text':
        return (
          <TextField
            fullWidth
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        );

      case 'phone':
        return (
          <PhoneInput
            value={typeof value === 'string' ? value : ''}
            onChange={(digits) => handleInputChange(field, digits)}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            placeholder={placeholder}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        );

      case 'date':
        return (
          <CustomDatePicker
            value={memoizedDateValue}
            onChange={(newValue) => {
              const formatted = newValue ? format(newValue, 'yyyy-MM-dd') : '';
              handleInputChange(field, formatted);
            }}
            error={!!error}
            helperText={error}
          />
        );

      case 'address':
        return (
          <GooglePlacesAutocomplete
            value={typeof value === 'string' ? value : ''}
            onChange={(address) => handleInputChange(field, address)}
            textFieldProps={{
              error: !!error,
              helperText: error,
              placeholder: placeholder || 'Enter property address',
              FormHelperTextProps: {
                sx: {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                }
              }
            }}
          />
        );

      case 'radio':
        return (
          <RadioGroup
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
          >
            {options?.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio sx={{
                  color: 'grey.500',
                  '&.Mui-checked': {
                    color: 'black',
                  },
                }} />}
                label={
                  <Box>
                    <Typography variant="body1">
                      {option.label}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        );

      case 'single_choice':
        return (
          <SingleChoiceQuestion
            options={options || []}
            value={typeof value === 'string' ? value : ''}
            onSelect={(selectedValue) => {
              handleInputChange(field, selectedValue);
              // Auto-advance to next question after state update
              setTimeout(() => {
                // For single choice, we know the value is valid since user just selected it
                if (currentStep < visibleQuestions.length - 1) {
                  const nextStep = currentStep + 1;
                  setCurrentStep(nextStep);

                  // Trigger background requests based on completed questions
                  const currentQuestionId = visibleQuestions[currentStep]?.id;

                  // Trigger fetchProximityData after address question is completed
                  if (currentQuestionId === 'address' && !backgroundRequestsTriggered.proximityData) {
                    setBackgroundRequestsTriggered(prev => ({ ...prev, proximityData: true }));
                    // Trigger in background without blocking
                    setTimeout(() => {
                      fetchProximityData();
                    }, 100);
                  }

                  if (currentQuestionId === 'minimumMonthlyIncome' && !backgroundRequestsTriggered.listingContent) {
                    setBackgroundRequestsTriggered(prev => ({ ...prev, listingContent: true }));
                    // Trigger in background without blocking
                    setTimeout(() => {
                      generateListingContent();
                    }, 100);
                  }

                  // Copy background content to display content when reaching generated content step
                  if (visibleQuestions[nextStep]?.type === 'generated_content' && backgroundGeneratedContent) {
                    setGeneratedContent(backgroundGeneratedContent);
                  }

                  // Also copy when moving from google_signin to generated_content
                  if (visibleQuestions[currentStep]?.type === 'google_signin' && visibleQuestions[nextStep]?.type === 'generated_content' && backgroundGeneratedContent) {
                    setGeneratedContent(backgroundGeneratedContent);
                  }
                }
              }, 50);
            }}
          />
        );

        case 'multi_select':
          if (field === 'petPolicy') {
            return (
              <PetPolicyQuestion
                options={options || []}
                value={value as { [key: string]: boolean }}
                onSelect={(selectedValues) => {
                  handleInputChange(field, selectedValues);
                }}
              />
            );
          } else {
            return (
              <MultiSelectQuestion
                options={options || []}
                value={value as string[] | { [key: string]: boolean }}
                onSelect={(selectedValues) => {
                  handleInputChange(field, selectedValues);
                }}
              />
            );
          }

      case 'switch':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={typeof value === 'boolean' ? value : false}
                onChange={(e) => handleInputChange(field, e.target.checked)}
                color="primary"
              />
            }
            label="Yes"
          />
        );

      case 'counter':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              onClick={() => {
                const currentValue = Number(typeof value === 'string' ? value : '0') || 0;
                const newValue = Math.max(0, currentValue - 1);
                handleInputChange(field, String(newValue));
              }}
              disabled={Number(typeof value === 'string' ? value : '0') <= 0}
              sx={{
                minWidth: '40px',
                height: '40px',
                p: 0,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                color: 'black',
                mx: 2
              }}
            >
              <RemoveIcon />
            </Button>
            <TextField
              value={typeof value === 'string' ? value : '0'}
              onChange={(e) => handleInputChange(field, e.target.value)}
              sx={{
                width: '70px',
                '& input': { textAlign: 'center' }
              }}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
            <Button
              onClick={() => {
                const currentValue = Number(typeof value === 'string' ? value : '0') || 0;
                handleInputChange(field, String(currentValue + 1));
              }}
              sx={{
                minWidth: '40px',
                height: '40px',
                p: 0,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                color: 'black',
                mx: 2
              }}
            >
              <AddIcon />
            </Button>
          </Box>
        );
      case 'proximity':
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Which nearby places should we include:
            </Typography>
            {isProximityLoading && proximityPlaces.length === 0 && (!proximityGroups || proximityGroups.length === 0) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ color: 'black', mb: 2 }} />
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  Finding nearby places...
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Usually takes 5 secs
                </Typography>
              </Box>
            ) : null}

            {proximityGroups && proximityGroups.length > 0 ? (
              <Box>
                {proximityGroups.map((group) => (
                  <Box key={group.category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'capitalize', mb: 1 }}>
                      {group.category.replace(/_/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      {group.places.map((place) => (
                        <FormControlLabel
                          key={place.id}
                          sx={{
                            m: 0,
                            alignItems: 'flex-start',
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: 'white',
                          }}
                          control={
                            <Checkbox
                              checked={place.enabled}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const checked = e.target.checked;
                                const updatedGroups = (proximityGroups || []).map(g => {
                                  if (g.category !== group.category) return g;
                                  return {
                                    ...g,
                                    places: g.places.map(p => p.id === place.id ? { ...p, enabled: checked } : p)
                                  };
                                });
                                setProximityGroups(updatedGroups);
                                const updatedPlaces = proximityPlaces.map(p => p.id === place.id ? { ...p, enabled: checked } : p);
                                setProximityPlaces(updatedPlaces);
                              }}
                              sx={{ mt: 0, color: 'grey.500', '&.Mui-checked': { color: 'black' } }}
                            />
                          }
                          label={
                            <Box sx={{ ml: 1, flex: 1, position: 'relative' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {editingPlaces[place.id] ? (
                                  <InputBase
                                    autoFocus
                                    value={place.name}
                                    onChange={(e) => {
                                      const newName = e.target.value;
                                      const updatedPlaces = proximityPlaces.map(p => p.id === place.id ? { ...p, name: newName } : p);
                                      setProximityPlaces(updatedPlaces);
                                      if (proximityGroups && proximityGroups.length > 0) {
                                        const updatedGroups = proximityGroups.map(g => ({
                                          ...g,
                                          places: g.places.map(p => p.id === place.id ? { ...p, name: newName } : p)
                                        }));
                                        setProximityGroups(updatedGroups);
                                      }
                                    }}
                                    onBlur={() => toggleEditing(place.id, false)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        (e.target as HTMLInputElement).blur();
                                      }
                                    }}
                                    fullWidth
                                    inputProps={{ 'aria-label': 'place name' }}
                                    sx={{
                                      fontWeight: 500,
                                      mb: 0.25,
                                      px: 0,
                                      py: 0,
                                      '& .MuiInputBase-input': { padding: 0 },
                                      borderBottom: '1px solid black'
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.25 }}>
                                    {place.name}
                                  </Typography>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => toggleEditing(place.id)}
                                  sx={{ ml: 'auto' }}
                                  aria-label="Edit place name"
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                {miles(place.distance)}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                {proximityPlaces.map((place) => (
                  <FormControlLabel
                    key={place.id}
                    sx={{
                      m: 0,
                      alignItems: 'flex-start',
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: 'white',
                    }}
                    control={
                      <Checkbox
                        checked={place.enabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const updatedPlaces = proximityPlaces.map((p) =>
                            p.id === place.id ? { ...p, enabled: e.target.checked } : p
                          );
                          setProximityPlaces(updatedPlaces);
                        }}
                        sx={{ mt: 0, color: 'grey.500', '&.Mui-checked': { color: 'black' } }}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {editingPlaces[place.id] ? (
                            <InputBase
                              autoFocus
                              value={place.name}
                              onChange={(e) => {
                                const newName = e.target.value;
                                const updatedPlaces = proximityPlaces.map(p => p.id === place.id ? { ...p, name: newName } : p);
                                setProximityPlaces(updatedPlaces);
                              }}
                              onBlur={() => toggleEditing(place.id, false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              fullWidth
                              inputProps={{ 'aria-label': 'place name' }}
                              sx={{
                                fontWeight: 500,
                                mb: 0.5,
                                px: 0,
                                py: 0,
                                '& .MuiInputBase-input': { padding: 0 },
                                borderBottom: '1px solid black'
                              }}
                            />
                          ) : (
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {place.name}
                            </Typography>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => toggleEditing(place.id)}
                            sx={{ ml: 'auto' }}
                            aria-label="Edit place name"
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {miles(place.distance)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      case 'availability':
        console.log('🎯 Rendering EditAvailability with schedule:', value);
        console.log('🎯 Current formData.availability:', formData.availability);
        return (
          <EditAvailability
            schedule={value as WeeklySchedule}
            timezone={formData.timezone}
            onChange={(newSchedule, newTimezone) => {
              console.log('🎯 EditAvailability onChange called with:', newSchedule, newTimezone);
              handleInputChange(field, newSchedule);
              handleInputChange('timezone', newTimezone);
            }}
          />
        );
      case 'google_signin':
        return (
          <GoogleSignInQuestion
            onSignInSuccess={handleGoogleSignIn}
            isAuthenticated={authIsAuthenticated}
          />
        );

      case 'screening_link':
        return (
          <ScreeningLinkQuestion
            screeningLink={screeningLink}
            createListingStatus={createListingStatus}
          />
        );

      case 'listing_photos':
        return (
          <Box>
            {listingPhotosError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {listingPhotosError}
              </Typography>
            )}
            <LandlordPhotoPicker
              pendingListingId={pendingListingId || ''}
              photos={pendingPhotos}
              onPhotosChange={setPendingPhotos}
              isEnsuringId={pendingListingIdLoading}
              ensureIdError={pendingListingIdError}
            />
          </Box>
        );

      case 'generated_content':
        return (
          <GeneratedContentQuestion
            backgroundContent={backgroundGeneratedContent}
            onCopyTitle={handleCopyTitle}
            onCopyDescription={handleCopyDescription}
            copiedTitle={copiedTitle}
            copiedDescription={copiedDescription}
            currentTitle={formData.title || ''}
            currentDescription={formData.description || ''}
            onTitleChange={(value) => handleInputChange('title', value)}
            onDescriptionChange={(value) => handleInputChange('description', value)}
          />
        );

      case 'dashboard':
        return (
          <DashboardReadyQuestion
            onGoToDashboard={handleGoToDashboard}
          />
        );

      default:
        return null;
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      id="onboarding-form"
      sx={{
        bgcolor: skipAvailability ? 'transparent' : 'grey.50',
        py: { xs: 6, md: 8 },
        borderTop: skipAvailability ? 'none' : '1px solid #e5e7eb'
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ maxWidth: skipAvailability ? '100%' : 600, mx: 'auto' }}>
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              color: 'grey.900'
            }}
          >
            Let's get started! 💪
          </Typography>

          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              border: '1px solid #e5e7eb'
            }}
          >

            {/* Question */}
            {currentQuestion && (
              <Box sx={{ mb: 4 }}>
                {currentQuestion.title && (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: currentQuestion.subtext ? 1 : 3,
                      color: 'grey.900'
                    }}
                  >
                    {currentQuestion.title}
                  </Typography>
                )}
                {currentQuestion.subtext && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'grey.600',
                      mb: 3,
                      fontSize: '0.875rem',
                      fontStyle: 'italic'
                    }}
                  >
                    {currentQuestion.subtext}
                  </Typography>
                )}
                {renderQuestion()}
              </Box>
            )}

            {/* Navigation buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: currentQuestion?.type === 'dashboard' ? 'flex-start' : 'space-between',
              mt: 4,
              gap: { xs: 3, md: 6 } // Add more gap on desktop
            }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={currentStep === 0}
                sx={{
                  borderColor: 'grey.300',
                  color: 'grey.700',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50'
                  }
                }}
              >
                Back
              </Button>

              {/* Only show Next button for non-single-choice questions and non-dashboard questions */}
              {currentQuestion?.type !== 'single_choice' && currentQuestion?.type !== 'dashboard' && (
                currentQuestion?.type === 'google_signin' ? (
                  !authIsAuthenticated ? (
                    <GoogleSignInButton googleCallback={handleGoogleSignIn} />
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        bgcolor: 'black',
                        color: 'white',
                        px: 3,
                        '&:hover': {
                          bgcolor: 'grey.900',
                          color: 'white'
                        }
                      }}
                    >
                      Create Link
                    </Button>
                  )
                ) : (
                  <Button
                    variant="contained"
                    endIcon={currentStep === visibleQuestions.length - 1 ? <CheckCircleIcon sx={{ color: '#fbbf24' }} /> : <ArrowForwardIcon sx={{ color: '#fbbf24' }} />}
                    onClick={handleNext}
                    sx={{
                      bgcolor: 'black',
                      color: 'white',
                      px: 3,
                      '&:hover': {
                        bgcolor: 'grey.900',
                        color: 'white'
                      }
                    }}
                  >
                    Next
                  </Button>
                )
              )}
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}


